import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { GraphTrendIconComponent } from '../../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { GroupedBarChartComponent } from '../../../../../../_shared/components/graphs/grouped-bar-chart/grouped-bar-chart.component';
import { calculateChartYTicks } from '../../../../../../_shared/helpers/chart-y-ticks.helper';
import { FilterService } from '../../../../../../_shared/services';
import { MaintenanceDashboardService } from '../../../services/maintenance-dashboard.service';
import { differenceInDays } from 'date-fns';
import { DateInterval } from '../../../../../../_shared/enums';
import { TimeSeriesGroupingHelper, GroupedChartData } from '../../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { MaintenanceInsightsResponse } from '../../../models/maintenance-dashboard-response.model';
import { AccountDateFilterRequest } from '../../../../../../_shared/models/accountDateFilter.model';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { EmptyStateComponent } from '../../../../../../_shared/components/states/empty-state/empty-state.component';
import { AssetsInMaintenanceModalComponent } from '../../modals/assets-in-maintenance-modal/assets-in-maintenance-modal.component';
import { Subject } from 'rxjs';
import { WidgetFilterData } from '../../../../../../_shared/models/filters/widget-filter-data.model';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-assets-in-maintenance',
  standalone: true,
  imports: [CommonModule, GraphTrendIconComponent, GroupedBarChartComponent, EmptyStateComponent],
  templateUrl: './assets-in-maintenance.component.html',
  styleUrl: './assets-in-maintenance.component.scss'
})
export class AssetsInMaintenanceComponent implements OnInit, OnDestroy {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private modalService = inject(NgbModal);
  private dashboardService = inject(MaintenanceDashboardService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private filterService = inject(FilterService);

  chartData: GroupedChartData[] = [];
  readonly barWidth = 12;
  readonly chartHeight = 175;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;
  readonly subgroups = [
    { name: 'Missed', legend: this.transloco.translate('maintenanceOverview.calendar.missedService'), color: '#FF7F00' },
    { name: 'Completed', legend: this.transloco.translate('maintenanceDashboard.charts.completedService'), color: '#50AD41' }
  ];

  title: string = this.transloco.translate('maintenanceDashboard.charts.assetsInMaintenance');
  isLoading: boolean = true;
  totalValue: number = 0;
  percentageChange: number = 0;
  widgetFilter: WidgetFilterData;

  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.filterService.filterUpdated$.subscribe(async (filter) => {
      if (Object.values(filter).every(x => x === null) || filter === null) return;

      this.widgetFilter = filter;
      await this.loadMaintenanceData();
    });
  }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;

    // Only load data if filter is initialized
    if (this.widgetFilter && !Object.values(this.widgetFilter).every(x => x === null)) {
      this.loadMaintenanceData();
    }
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async loadMaintenanceData(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();

      let request: AccountDateFilterRequest = {
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        accountIds: this.widgetFilter.accountIds
      };

      const [summaryResponse, missedResponse, completedResponse] = await Promise.all([
        this.dashboardService.getAssetsInMaintenanceSummary({
          ...request,
          previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
          previousTimeTo: this.widgetFilter.previousCycleEndDate!
        }, this.cancelSubject),
        this.dashboardService.getMissedServiceInsights(request, this.cancelSubject),
        this.dashboardService.getCompletedServiceInsights(request, this.cancelSubject)
      ]);

      const missedData = missedResponse.data ?? [];
      const completedData = completedResponse.data ?? [];
      const summary = summaryResponse.data;

      this.chartData = this.processResponses(missedData, completedData);
      this.yTicks = this.calculateYTicks(this.chartData);
      this.totalValue = summary?.totalValue ?? 0;
      this.percentageChange = summary?.percentageChange ?? 0;

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  private processResponses(missedData: MaintenanceInsightsResponse[], completedData: MaintenanceInsightsResponse[]): GroupedChartData[] {
    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);
    const groupingType = this.determineGroupingType(daysDifference, this.widgetFilter.interval);

    const datasets = [
      { data: missedData, label: 'Missed' },
      { data: completedData, label: 'Completed' }
    ];

    switch (groupingType) {
      case 'hourly': return TimeSeriesGroupingHelper.groupByHours(datasets);
      case 'daily': return TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
      default: return TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }
  }

  private determineGroupingType(daysDifference: number, interval: DateInterval): 'hourly' | 'daily' | 'weekly' {
    if (daysDifference === 0) return 'hourly';
    if (daysDifference <= 7) return 'daily';
    return 'weekly';
  }

  private calculateYTicks(chartData: GroupedChartData[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const allValues = chartData.flatMap(item => [
      Number(item['Missed']) || 0,
      Number(item['Completed']) || 0
    ]);

    return calculateChartYTicks(allValues, this.defaultYTicks);
  }

  formatValue(): string {
    return `${this.totalValue} Assets`;
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return false;
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      // Check all subgroups for non-zero values
      return Object.keys(item).some(key => {
        if (key === 'date' || key === 'originalTimestamp' || key === 'displayLabel' || key === 'tooltipDateTime') return false;
        const value = Number(item[key]) || 0;
        return value > 0;
      });
    });
  }

  openModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };

    this.modalService.open(AssetsInMaintenanceModalComponent, modalOptions);
  }
}